import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MaskLines } from "./motion";

// Reusable dark navy hero for interior pages
export const PageHero = ({ eyebrow, titleLines, tagline, subtitle, cta, image, imageAlt = "" }) => (
  <section className="relative min-h-[62vh] flex items-end overflow-hidden bg-navy" data-testid="page-hero">
    {image && (
      <motion.img
        src={image}
        alt={imageAlt}
        loading="eager"
        initial={{ scale: 1.12 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
        className="absolute inset-0 w-full h-full object-cover opacity-40"
      />
    )}
    <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/40" />
    <div className="relative max-w-[1440px] w-full mx-auto px-6 md:px-12 pb-16 md:pb-24 pt-40">
      {eyebrow && (
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="eyebrow mb-6"
        >
          {eyebrow}
        </motion.p>
      )}
      <h1 className="font-serif font-light text-white text-5xl sm:text-6xl md:text-7xl leading-[0.98] tracking-tight max-w-4xl">
        <MaskLines lines={titleLines} delay={0.15} />
      </h1>
      {tagline && (
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.45 }}
          className="mt-6 font-serif font-light italic text-gold text-2xl md:text-3xl max-w-2xl leading-snug"
        >
          {tagline}
        </motion.p>
      )}
      {subtitle && (
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="mt-6 text-white/70 max-w-xl text-base md:text-lg leading-relaxed font-light"
        >
          {subtitle}
        </motion.p>
      )}
      {cta && (
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.7 }}
          className="mt-9"
        >
          {cta.to.startsWith("#") ? (
            <a
              href={cta.to}
              data-testid="page-hero-cta"
              className="group inline-flex items-center gap-3 bg-gold text-white px-8 py-4 text-[0.72rem] font-semibold uppercase tracking-[0.2em] hover:bg-gold-dark transition-colors duration-300"
            >
              {cta.label}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </a>
          ) : (
            <Link
              to={cta.to}
              data-testid="page-hero-cta"
              className="group inline-flex items-center gap-3 bg-gold text-white px-8 py-4 text-[0.72rem] font-semibold uppercase tracking-[0.2em] hover:bg-gold-dark transition-colors duration-300"
            >
              {cta.label}
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          )}
        </motion.div>
      )}
    </div>
  </section>
);
